import { CongSetting, CongSettings } from "../../../types/scheduling/settings";
import { useTranslation } from "react-i18next";
import { useTags } from "../../../api/tags";
import { useState } from "react";
import { tagDefaultToTag, TagsDDM, tagToDefault, terr_checkout_default_tags } from "../../congregation/tags";
import { PseudoTag, Tag, TagCategory, TagDefault } from "../../../types/scheduling/tags";
import useRunOnce from "../../../helpers/useRunOnce";
import { Card, Col, Form, Row } from "react-bootstrap";
import { CheckBox, DDMnumbers, DDMstrings, TextInput } from "./settings";

export const SettingsTerr = (props: {
  settings: CongSettings;
  setSettings: (set: CongSettings) => void;
  tileUrlRef?: React.RefObject<HTMLInputElement | null>;
  styleUrlRef?: React.RefObject<HTMLInputElement | null>;
}) => {
  const { t } = useTranslation();
  const tags = useTags();
  const [defaultCheckout, setDefaultCheckout] = useState(terr_checkout_default_tags(props.settings));
  const tileProvider = props.settings.terr_tile_provider;
  // there are some tile providers that we can't use on mobile, mainly because they are vector-only
  const unsupportedCustomMobileTileProvider = (): boolean => {
    return tileProvider === 4;
  };
  if (unsupportedCustomMobileTileProvider() && props.settings.terr_custom_mobile_tiles) {
    props.setSettings({ ...props.settings, terr_custom_mobile_tiles: false });
  }

  const defaultTags = defaultCheckout.flatMap(
    (td) => tagDefaultToTag(td, tags.data ?? [], TagCategory.Territory) ?? [],
  );

  useRunOnce(() => {
    // convert setting to TagDefault[], so it can be saved
    props.setSettings({
      ...props.settings,
      terr_checkout_default_tag: defaultCheckout,
    });
  });

  return (
    <div className="d-flex flex-column gap-3">
      <Card className="p-1">
        <Card.Header>{t("general.general")}</Card.Header>
        <Card.Body>
          <CheckBox
            switchType
            settingKey={CongSetting.TerrPubViewAll}
            settings={props.settings}
            setSettings={props.setSettings}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrSortByLocality}
            settings={props.settings}
            setSettings={props.setSettings}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrSharing}
            settings={props.settings}
            setSettings={props.setSettings}
          />
        </Card.Body>
      </Card>

      <Card className="p-1">
        <Card.Header>{t("schedules.territory.records")}</Card.Header>
        <Card.Body>
          <DDMnumbers
            settingKey={CongSetting.TerrDisplayCount}
            settings={props.settings}
            setSettings={props.setSettings}
            options={0}
            exactOptions={[5, 10, 15, 20, 25, 30, 40, 50, 500]}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrAllowRequests}
            settings={props.settings}
            setSettings={props.setSettings}
          />
          <TextInput
            settingKey={CongSetting.TerrMaxRequests}
            settings={props.settings}
            setSettings={props.setSettings}
            inputType="number"
            min={0}
            max={999}
            disabled={!props.settings.terr_allow_requests}
            infoMsg={t("settings.territory.disable-requests-after-checkout-info")}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrPubNoReturn}
            settings={props.settings}
            setSettings={(settings) => {
              if (settings.terr_pub_no_return) {
                settings.terr_pub_return_unworked = false;
              }
              props.setSettings(settings);
            }}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrPubReturnUnworked}
            settings={props.settings}
            setSettings={props.setSettings}
            disabled={props.settings.terr_pub_no_return}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrPubAllowCheckout}
            settings={props.settings}
            setSettings={props.setSettings}
          />
          <TextInput
            settingKey={CongSetting.TerrCheckoutDurationDays}
            settings={props.settings}
            setSettings={props.setSettings}
            inputType="number"
            min={1}
            max={365}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrCheckoutHideMore}
            settings={props.settings}
            setSettings={props.setSettings}
          />
          <Form.Group as={Row} className="mt-1" controlId="setting-terr-checkout-default-tag">
            <Form.Label column>{t("schedules.settings.territory.default-tag-checkout")}</Form.Label>
            <Col>
              <TagsDDM
                tags={tags.data?.filter((t) => t.category === TagCategory.Territory) ?? []}
                showAllTag
                showUntaggedTag
                showNonBusinessTag
                selected={defaultTags}
                setSelected={(t: (Tag | PseudoTag)[]) => {
                  const selectedDefaults: TagDefault[] = t.flatMap((tag: Tag | PseudoTag) => tagToDefault(tag) ?? []);
                  props.setSettings({
                    ...props.settings,
                    terr_checkout_default_tag: selectedDefaults,
                  });
                  setDefaultCheckout(selectedDefaults);
                }}
              />
            </Col>
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="p-1">
        <Card.Header>{t("general.map")}</Card.Header>
        <Card.Body>
          <DDMstrings
            settings={props.settings}
            setSettings={props.setSettings}
            settingKey={CongSetting.TerrTileProvider}
            coerceToNumber
            options={[
              { label: "MapTiler Basic", value: "0" },
              { label: "Thunderforest Transport", value: "1" },
              { label: "MapTiler OpenStreetMaps", value: "7" },
              { label: "MapTiler Streets", value: "3" },
              { label: "HERE Explore", value: "4" },
              { label: "MapBox Streets", value: "9" },
              { label: "MapTiler Satellite", value: "5" },
              { label: "MapTiler Hybrid Satellite", value: "8" },
              { label: "MapBox Satellite Streets", value: "10" },
              { label: "Esri Imaging (Satellite)", value: "6" },
              { label: "OpenStreetMaps", value: "2" },
              { label: t("general.custom"), value: "999" },
            ]}
          />
          <TextInput
            disabled={props.settings.terr_tile_provider !== 999}
            inputType="text"
            settingKey={CongSetting.TerrCustomTileProviderTileUrl}
            settings={props.settings}
            setSettings={props.setSettings}
            infoMsg={t("schedules.settings.territory.custom-provider-tile-url-info")}
            min={10}
            max={512}
            required={props.settings.terr_tile_provider === 999}
            pattern={props.settings.terr_tile_provider === 999 ? "https://.*" : undefined}
            inputRef={props.tileUrlRef}
          />
          <TextInput
            disabled={props.settings.terr_tile_provider !== 999}
            inputType="text"
            settingKey={CongSetting.TerrCustomTileProviderStyleUrl}
            settings={props.settings}
            setSettings={props.setSettings}
            infoMsg={t("schedules.settings.territory.custom-provider-style-url-info")}
            min={10}
            max={512}
            required={props.settings.terr_tile_provider === 999}
            pattern={props.settings.terr_tile_provider === 999 ? "https://.*" : undefined}
            inputRef={props.styleUrlRef}
          />
          <TextInput
            disabled={
              !props.settings.terr_custom_tile_provider_tile_url || !props.settings.terr_custom_tile_provider_style_url
            }
            inputType="text"
            settingKey={CongSetting.TerrCustomTileProviderAttribution}
            settings={props.settings}
            setSettings={props.setSettings}
            infoMsg={t("schedules.settings.territory.custom-provider-attribution-info")}
            min={3}
            max={256}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrCustomMobileTileProvider}
            settings={props.settings}
            setSettings={props.setSettings}
            infoMsg={t("schedules.settings.territory.mobile-tiles-info")}
          />
        </Card.Body>
      </Card>

      <Card className="p-1">
        <Card.Header>{t("schedules.territory.addresses")}</Card.Header>
        <Card.Body>
          <CheckBox
            switchType
            settingKey={CongSetting.TerrAddressHideType}
            settings={props.settings}
            setSettings={props.setSettings}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrPubUpdateAddresses}
            settings={props.settings}
            setSettings={(settings) => {
              if (!settings.terr_pub_update_address) {
                settings.terr_pub_delete_address = false;
                settings.terr_pub_update_addr_tags = false;
              }
              props.setSettings(settings);
            }}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrPubDeleteAddresses}
            settings={props.settings}
            setSettings={props.setSettings}
            disabled={!props.settings.terr_pub_update_address}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrPubUpdateAddrTags}
            settings={props.settings}
            setSettings={props.setSettings}
            disabled={!props.settings.terr_pub_update_address}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrAddressH2H}
            settings={props.settings}
            setSettings={props.setSettings}
            infoMsg={t("schedules.settings.territory.cong-h2h-info")}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrAddressVisits}
            settings={props.settings}
            setSettings={(settings) => {
              if (!settings.terr_address_visits) {
                settings.terr_address_visits_reset_checkin = false;
                settings.terr_address_hide_visited_map = false;
              }
              props.setSettings(settings);
            }}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrAddressHideVisitedMap}
            settings={props.settings}
            setSettings={props.setSettings}
            disabled={!props.settings.terr_address_visits}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrAddressVisitsResetCheckin}
            settings={props.settings}
            setSettings={props.setSettings}
            disabled={!props.settings.terr_address_visits}
          />
          <CheckBox
            switchType
            settingKey={CongSetting.TerrEmphasizeAddrNames}
            settings={props.settings}
            setSettings={props.setSettings}
          />
        </Card.Body>
      </Card>
    </div>
  );
};
